/**
 * LayoutSidebar — sticky left nav panel.
 *
 * Shows "All" and "Favourites" at the top, then categories grouped by their
 * section field with live entity-count badges. Mirrors the WorkflowsPage
 * sidebar pattern exactly.
 */

import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import AppsIcon from '@mui/icons-material/Apps';
import StarIcon from '@mui/icons-material/Star';
import CategoryIcon from '@mui/icons-material/Category';
import { renderCategoryIcon } from '../../iconRegistry';
import type { LayoutCategory } from '../../types';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  sidebar: {
    width: 220,
    flexShrink: 0,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    position: 'sticky' as const,
    top: 0,
  },
  sidebarLabel: {
    display: 'block',
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
  },
  sidebarList: {
    padding: theme.spacing(0, 0, 1),
  },
  navItem: {
    width: 'auto',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.2, 1),
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  navItemActive: {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.18)' : '#FDF0F8',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.24)' : '#FAE3F3',
    },
  },
  navIcon: { minWidth: 28 },
  navIconActive: { color: theme.palette.primary.main, fontSize: 18 },
  navIconInactive: { color: theme.palette.text.secondary, fontSize: 18 },
  navIconStar: { color: '#F59E0B', fontSize: 18 },
  navText: { margin: 0, '& span': { fontSize: 13, fontWeight: 500 } },
  navTextActive: { '& span': { color: theme.palette.primary.main, fontWeight: 700 } },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: theme.shape.borderRadius,
    padding: '1px 7px',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#E9ECEF',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap' as const,
    transition: 'background 0.12s, color 0.12s',
    marginRight: theme.spacing(1),
  },
  badgeActive: { backgroundColor: theme.palette.primary.main, color: '#fff' },
}));

// ---------------------------------------------------------------------------
// NavItem
// ---------------------------------------------------------------------------

interface NavItemProps {
  label: string;
  iconEl: React.ReactNode;
  count: number;
  active: boolean;
  tooltip?: string;
  onClick: () => void;
}

function NavItem({ label, iconEl, count, active, tooltip, onClick }: NavItemProps) {
  const { classes } = useStyles();
  const item = (
    <ListItem
      disableGutters
      className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
      onClick={onClick}
    >
      <ListItemIcon className={classes.navIcon}>{iconEl}</ListItemIcon>
      <ListItemText
        primary={label}
        className={`${classes.navText} ${active ? classes.navTextActive : ''}`}
      />
      {count >= 0 && (
        <span className={`${classes.badge} ${active ? classes.badgeActive : ''}`}>
          {count}
        </span>
      )}
    </ListItem>
  );
  return tooltip ? (
    <Tooltip title={tooltip} placement="right" arrow>
      {item}
    </Tooltip>
  ) : (
    item
  );
}

// ---------------------------------------------------------------------------
// LayoutSidebar props
// ---------------------------------------------------------------------------

interface LayoutSidebarProps {
  categories: LayoutCategory[];
  countByCategory: Record<string, number>;
  totalCount: number;
  favouriteCount: number;
  selectedId: string;
  contentTypeLabel: string;
  onSelect: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LayoutSidebar({
  categories,
  countByCategory,
  totalCount,
  favouriteCount,
  selectedId,
  contentTypeLabel,
  onSelect,
}: LayoutSidebarProps) {
  const { classes } = useStyles();

  // Collect unique sections (preserving insertion order)
  const sections = Array.from(
    new Set(
      categories
        .map(c => c.section ?? 'other')
        .filter(s => s !== ''),
    ),
  );

  return (
    <Paper className={classes.sidebar} elevation={0}>
      {/* Library section */}
      <Typography component="span" className={classes.sidebarLabel}>
        Library
      </Typography>
      <List disablePadding className={classes.sidebarList}>
        <NavItem
          label="Favourites"
          iconEl={
            <StarIcon
              className={
                selectedId === 'favourites'
                  ? classes.navIconActive
                  : classes.navIconStar
              }
            />
          }
          count={favouriteCount}
          active={selectedId === 'favourites'}
          onClick={() => onSelect('favourites')}
          tooltip="Your starred items"
        />
        <NavItem
          label={`All ${contentTypeLabel}`}
          iconEl={
            <AppsIcon
              className={
                selectedId === 'all'
                  ? classes.navIconActive
                  : classes.navIconInactive
              }
            />
          }
          count={totalCount}
          active={selectedId === 'all'}
          onClick={() => onSelect('all')}
        />
      </List>

      {/* One section per distinct category.section value */}
      {sections.map(section => {
        const sectionCats = categories.filter(
          c => (c.section ?? 'other') === section,
        );
        if (sectionCats.length === 0) return null;
        return (
          <div key={section}>
            <Divider />
            <Typography component="span" className={classes.sidebarLabel}>
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </Typography>
            <List disablePadding className={classes.sidebarList}>
              {sectionCats.map(cat => {
                const isActive = selectedId === cat.id;
                return (
                  <NavItem
                    key={cat.id}
                    label={cat.name}
                    iconEl={
                      renderCategoryIcon(cat.icon, {
                        style: { fontSize: 18 },
                        className: isActive ? classes.navIconActive : classes.navIconInactive,
                      }) ?? (
                        <CategoryIcon
                          style={{ fontSize: 18 }}
                          className={isActive ? classes.navIconActive : classes.navIconInactive}
                        />
                      )
                    }
                    count={countByCategory[cat.id] ?? 0}
                    active={isActive}
                    onClick={() => onSelect(cat.id)}
                    tooltip={cat.description}
                  />
                );
              })}
            </List>
          </div>
        );
      })}

      {/* Categories with no section */}
      {(() => {
        const unsectioned = categories.filter(c => !c.section);
        if (unsectioned.length === 0) return null;
        return (
          <div>
            <Divider />
            <Typography component="span" className={classes.sidebarLabel}>
              Other
            </Typography>
            <List disablePadding className={classes.sidebarList}>
              {unsectioned.map(cat => {
                const isActive = selectedId === cat.id;
                return (
                  <NavItem
                    key={cat.id}
                    label={cat.name}
                    iconEl={
                      renderCategoryIcon(cat.icon, {
                        style: { fontSize: 18 },
                        className: isActive ? classes.navIconActive : classes.navIconInactive,
                      }) ?? (
                        <CategoryIcon
                          style={{ fontSize: 18 }}
                          className={isActive ? classes.navIconActive : classes.navIconInactive}
                        />
                      )
                    }
                    count={countByCategory[cat.id] ?? 0}
                    active={isActive}
                    onClick={() => onSelect(cat.id)}
                    tooltip={cat.description}
                  />
                );
              })}
            </List>
          </div>
        );
      })()}
    </Paper>
  );
}
